import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, Trophy, Loader2 } from "lucide-react";
import { gamesApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Leaderboard from "@/components/games/Leaderboard";

// Shared shell for Snake / Block / Race / Duck — handles auth gate, free-plays/unlock, score submit.
export default function ArcadeShell({ title, game, stateKey, stateFn, unlockFn, scoreFn, children }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, signIn } = useAuth();
  const [playing, setPlaying] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const configQuery = useQuery({ queryKey: ["gamesConfig"], queryFn: gamesApi.getConfig });
  const speed = configQuery.data?.[game]?.speed || "normal";
  const stateQuery = useQuery({ queryKey: [stateKey], queryFn: stateFn, enabled: !!user?.phone });
  const state = stateQuery.data;
  const canPlay = !!state && (state.unlocked_today || state.free_plays_left > 0);

  const unlock = useMutation({
    mutationFn: unlockFn,
    onSuccess: () => {
      toast.success("Unlocked — unlimited plays today!");
      queryClient.invalidateQueries({ queryKey: [stateKey] });
      queryClient.invalidateQueries({ queryKey: ["gamesState"] });
    },
    onError: (e) => toast.error(e.message || "Could not unlock"),
  });

  const submitScore = useMutation({
    mutationFn: ({ score, extra }) => scoreFn(score, extra),
    onSuccess: (res) => {
      setLastResult(res);
      if (res.points_awarded_delta > 0) toast.success(`+${res.points_awarded_delta} points!`);
      queryClient.invalidateQueries({ queryKey: [stateKey] });
      queryClient.invalidateQueries({ queryKey: ["gamesState"] });
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard", game] });
    },
    onError: (e) => toast.error(e.message || "Could not save score"),
  });

  const onGameOver = (score, extra) => {
    setPlaying(false);
    submitScore.mutate({ score, extra });
  };

  // For self-contained games (e.g. Car Race) that submit a score per round yet keep playing.
  const submitScoreOnly = (score, extra) => submitScore.mutate({ score, extra });

  return (
    <div className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 py-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/games")} className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-secondary"><ChevronLeft className="h-6 w-6" /></button>
        <h1 className="font-display text-xl font-bold">{title}</h1>
        {speed !== "normal" ? <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium capitalize text-muted-foreground">{speed}</span> : null}
      </div>

      {!user ? (
        <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl bg-secondary p-8 text-center">
          <p className="font-display text-lg font-bold">Sign in to play</p>
          <button onClick={signIn} className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">Sign in with Google</button>
        </div>
      ) : !user.phone ? (
        <div className="mt-10 rounded-2xl bg-secondary p-8 text-center">
          <p className="font-display text-lg font-bold">Add your phone number</p>
          <p className="text-sm text-muted-foreground">Add a mobile number in your profile to earn points.</p>
          <button onClick={() => navigate("/profile")} className="mt-3 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Go to profile</button>
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-center justify-between rounded-2xl bg-primary p-4 text-primary-foreground">
            <div>
              <p className="text-xs opacity-80">Balance</p>
              <p className="font-display text-xl font-bold">{state?.points_balance ?? 0} pts</p>
            </div>
            <div className="text-right text-xs opacity-90">
              <p className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5" /> Best today: {state?.best_score_today ?? 0}</p>
              <p>All-time: {state?.alltime_best ?? 0}</p>
              <p>{state?.unlocked_today ? "Unlimited today" : `${state?.free_plays_left ?? 0} free plays left`}</p>
            </div>
          </div>

          {lastResult ? (
            <p className="mt-3 rounded-xl bg-[hsl(var(--brand-tertiary))] px-4 py-2 text-center text-sm font-medium text-primary">
              Score saved! Best today: {lastResult.best_score_today} · {lastResult.points_earned_today} pts earned today
            </p>
          ) : null}

          <div className="mt-4 flex flex-1 flex-col items-center justify-center">
            {playing ? (
              children({ onGameOver, submitScore: submitScoreOnly, speed })
            ) : stateQuery.isPending ? (
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            ) : canPlay ? (
              <button onClick={() => { setLastResult(null); setPlaying(true); }} data-testid="arcade-start" className="rounded-full bg-primary px-8 py-4 font-display text-lg font-bold text-primary-foreground">Start game</button>
            ) : (
              <div className="flex flex-col items-center gap-3 text-center">
                <p className="text-sm text-muted-foreground">Out of free plays for today.</p>
                <button onClick={() => unlock.mutate()} disabled={unlock.isPending || (state?.points_balance ?? 0) < (state?.unlock_cost ?? 0)} data-testid="arcade-unlock" className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                  {(state?.points_balance ?? 0) < (state?.unlock_cost ?? 0) ? "Not enough points" : `Unlock unlimited for ${state?.unlock_cost} pts`}
                </button>
              </div>
            )}
          </div>

          {!playing ? <div className="mt-5"><Leaderboard game={game} phone={user?.phone || ""} /></div> : null}
        </>
      )}
    </div>
  );
}
